import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Plus, Trash2 } from "lucide-react";

/** Fetches custom field definitions once and keeps them in local state so the
 * parent can render them next to its own built-in fields. */
export function useCustomFields(scope) {
  const [fields, setFields] = useState([]);
  const load = () =>
    api.get("/custom-fields", { params: { scope } })
      .then((r) => setFields(r.data))
      .catch(() => {});
  useEffect(() => { if (scope) load(); }, [scope]);
  return { fields, reload: load };
}

/** Renders the user-defined inputs for a form. Writes into `values` (a plain
 * object keyed by field.key) via `onChange`. */
export function CustomFieldsInputs({ scope, values, onChange }) {
  const { fields } = useCustomFields(scope);
  if (!fields.length) return null;
  const set = (k, v) => onChange({ ...(values || {}), [k]: v });
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid={`custom-fields-${scope}`}>
      <div className="eyebrow text-slate-700">Custom fields</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((f) => {
          const v = (values || {})[f.key] ?? "";
          const tid = `cf-${scope}-${f.key}`;
          return (
            <div key={f.field_id}>
              <Label className="text-xs">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              {f.type === "text" && (
                <Input value={v} onChange={(e) => set(f.key, e.target.value)} data-testid={tid} />
              )}
              {f.type === "number" && (
                <Input type="number" step="0.01" value={v} onChange={(e) => set(f.key, e.target.value)} data-testid={tid} />
              )}
              {f.type === "date" && (
                <Input type="date" value={v} onChange={(e) => set(f.key, e.target.value)} data-testid={tid} />
              )}
              {f.type === "select" && (
                <Select value={v} onValueChange={(x) => set(f.key, x)}>
                  <SelectTrigger data-testid={tid}><SelectValue placeholder="Pick one..." /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Admin CRUD panel: define/edit/delete the extra fields that will appear on
 * driver and trip forms. Dropped into Settings so buyers can tailor the forms
 * to their fleet (trailer #, DOT medical expiry, fuel card last 4, etc.). */
export function CustomFieldsManager() {
  const [scope, setScope] = useState("driver");
  const { fields, reload } = useCustomFields(scope);
  const [draft, setDraft] = useState({ label: "", type: "text", options: "", required: false });

  const add = async () => {
    const label = draft.label.trim();
    if (!label) { toast.error("Label is required"); return; }
    const body = {
      label, scope, type: draft.type, required: !!draft.required,
      options: draft.type === "select"
        ? draft.options.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
    };
    try {
      await api.post("/custom-fields", body);
      toast.success("Field added");
      setDraft({ label: "", type: "text", options: "", required: false });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add field");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this field? Values already saved on existing records are kept.")) return;
    await api.delete(`/custom-fields/${id}`);
    toast.success("Field removed");
    reload();
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="custom-fields-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Custom Fields</div>
          <div className="text-xs text-slate-500 mt-0.5">Add extra fields (trailer #, DOT medical expiry, fuel card last 4, etc.) to your Driver and Trip forms.</div>
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-40" data-testid="cf-scope-picker"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="driver">Driver fields</SelectItem>
            <SelectItem value="trip">Trip fields</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 space-y-2">
        {fields.length === 0 && <div className="text-xs text-slate-500 italic">No custom fields yet. Add one below.</div>}
        {fields.map((f) => (
          <div key={f.field_id} className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{f.label} {f.required && <span className="text-red-500 text-xs">required</span>}</div>
              <div className="text-[11px] text-slate-500">
                Type: <code className="font-mono">{f.type}</code> · Key: <code className="font-mono">{f.key}</code>
                {f.type === "select" && f.options?.length ? ` · Options: ${f.options.join(", ")}` : ""}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(f.field_id)} data-testid={`cf-del-${f.key}`}>
              <Trash2 size={14} className="text-red-500" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded border border-dashed border-slate-300 p-3">
        <div className="text-xs font-semibold text-slate-700 mb-2">Add a new {scope === "driver" ? "driver" : "trip"} field</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-5">
            <Label className="text-[11px]">Label</Label>
            <Input value={draft.label} placeholder="e.g. Trailer #" onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="cf-new-label" />
          </div>
          <div className="md:col-span-3">
            <Label className="text-[11px]">Type</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger data-testid="cf-new-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="select">Dropdown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-[11px]">{draft.type === "select" ? "Options (comma separated)" : "Required?"}</Label>
            {draft.type === "select" ? (
              <Input value={draft.options} placeholder="Dry van, Reefer, Flatbed" onChange={(e) => setDraft({ ...draft, options: e.target.value })} data-testid="cf-new-options" />
            ) : (
              <label className="flex items-center gap-2 h-9 pl-1 text-sm">
                <input type="checkbox" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} data-testid="cf-new-required" />
                Required
              </label>
            )}
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={add} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cf-add-btn">
            <Plus size={14} className="mr-1" /> Add field
          </Button>
        </div>
      </div>
    </div>
  );
}
